/**
 * PDF Export Controller
 *
 * Handles context-aware PDF export functionality for different pages
 * in the Trip Planner application. Determines the current page context
 * and triggers appropriate PDF generation using the data-management services.
 *
 * Features:
 * - Context detection (Days, Places, Travel, Accommodation, Documents, etc.)
 * - PDF generation for specific content types using jsPDF
 * - User-friendly confirmation modal and progress tracking
 * - Error handling and user feedback
 * - Integration with data-management package services
 */

import { pdfExportService } from '@trip-planner-monorepo/app-features';

class PDFExportController {
  constructor() {
    this.supportedPages = [
      'Days List',
      'Day Detail',
      'Places List',
      'Place Detail',
      'Travel List',
      'Travel Detail',
      'Accommodation List',
      'Accommodation Detail',
      'Documents List',
      'Document Detail',
      'Extras List'
    ];

    this.isExporting = false;
    this.currentModal = null;
  }

  /**
   * Main export method - shows confirmation modal and handles PDF export
   *
   * @param {Object} context - Current application context
   * @param {string} context.pathname - Current route pathname
   * @param {Object} context.trip - Current trip data
   * @param {Object} context.user - Current user data
   * @returns {Promise<boolean>} Success status
   */
  async exportCurrentPage(context) {
    if (this.isExporting) {
      console.warn('PDF export already in progress');
      return false;
    }

    try {
      // Get export preview information
      const previewInfo = await pdfExportService.getExportPreview(context);

      // Show confirmation modal
      return await this.showExportConfirmation(context, previewInfo);

    } catch (error) {
      console.error('PDF Export failed:', error);
      this.showExportErrorNotification(error.message);
      return false;
    }
  }

  /**
   * Show export confirmation modal
   * @param {Object} context - Application context
   * @param {Object} previewInfo - Export preview information
   * @returns {Promise<boolean>} Export success status
   */
  async showExportConfirmation(context, previewInfo) {
    return new Promise((resolve) => {
      // Create modal element and inject into DOM
      const modalContainer = this.createModalContainer();

      // Import the PDFConfirmationModal component dynamically
      this.loadPDFConfirmationModal().then(({ PDFConfirmationModal: _PDFConfirmationModal }) => {
        // Render the modal using React (assuming React is available)
        const modalProps = {
          isOpen: true,
          onClose: () => {
            this.closeModal(modalContainer);
            resolve(false);
          },
          onConfirm: async () => {
            try {
              await this.performExport(context, modalContainer);
              resolve(true);
            } catch (error) {
              console.error('Export failed:', error);
              this.showExportErrorNotification(error.message);
              this.closeModal(modalContainer);
              resolve(false);
            }
          },
          previewInfo: previewInfo,
          isGenerating: false,
          generationProgress: 0
        };

        // For now, we'll create a simple HTML modal instead of using React directly
        this.createHTMLConfirmationModal(modalContainer, modalProps, context);
      });
    });
  }

  /**
   * Perform the actual PDF export with progress tracking
   * @param {Object} context - Application context
   * @param {HTMLElement} modalContainer - Modal container element
   */
  async performExport(context, modalContainer) {
    this.isExporting = true;

    try {
      // Update modal to show progress
      this.updateModalProgress(modalContainer, 10, 'Starting export...');

      // Perform the actual export
      this.updateModalProgress(modalContainer, 50, 'Generating PDF...');

      const exportResult = await pdfExportService.exportCurrentPage(context);

      this.updateModalProgress(modalContainer, 100, 'Complete!');

      // Show success notification
      this.showExportCompletedNotification(exportResult.pageType, exportResult.filename);

      // Close modal after brief delay
      setTimeout(() => {
        this.closeModal(modalContainer);
      }, 1500);

    } catch (error) {
      console.error('PDF export failed:', error);
      // Handle error and re-throw for caller
      throw error;
    } finally {
      this.isExporting = false;
    }
  }

  /**
   * Create modal container element
   * @returns {HTMLElement} Modal container
   */
  createModalContainer() {
    const container = document.createElement('div');
    container.id = 'pdf-export-modal-container';
    document.body.appendChild(container);
    this.currentModal = container;
    return container;
  }

  /**
   * Load PDF confirmation modal component dynamically
   * @returns {Promise<Object>} Modal component
   */
  async loadPDFConfirmationModal() {
    // For now, return a mock component since we're not using React directly in the controller
    return {
      PDFConfirmationModal: null
    };
  }

  /**
   * Create HTML-based confirmation modal
   * @param {HTMLElement} container - Container element
   * @param {Object} props - Modal props
   * @param {Object} context - Application context
   */
  createHTMLConfirmationModal(container, props, context) {
    const { previewInfo, onConfirm, onClose } = props;

    const modalHTML = `
      <div class="pdf-confirmation-overlay" id="pdf-modal-overlay">
        <div class="pdf-confirmation-modal">
          <!-- Header -->
          <div class="pdf-confirmation-header">
            <h3>Save PDF file</h3>
            <button class="pdf-confirmation-close" onclick="window.pdfExportController.closeCurrentModal()">×</button>
          </div>

          <!-- Content -->
          <div class="pdf-confirmation-content">
            ${previewInfo?.available ? `
              <div class="pdf-confirmation-info">
                <div class="pdf-info-item">
                  <span class="pdf-info-label">Page Type:</span>
                  <span class="pdf-info-value">${previewInfo.pageType}</span>
                </div>
                <div class="pdf-info-item">
                  <span class="pdf-info-label">Content:</span>
                  <span class="pdf-info-value">${previewInfo.contentSummary}</span>
                </div>
                <div class="pdf-info-item">
                  <span class="pdf-info-label">Estimated Length:</span>
                  <span class="pdf-info-value">${previewInfo.estimatedPages} ${previewInfo.estimatedPages === 1 ? 'page' : 'pages'}</span>
                </div>
                <div class="pdf-info-item">
                  <span class="pdf-info-label">Filename:</span>
                  <span class="pdf-info-value pdf-filename">${previewInfo.filename}</span>
                </div>
              </div>
              <div class="pdf-confirmation-message">
                <p>Are you ready to generate and download this PDF?</p>
              </div>
              <div class="pdf-progress-container" id="pdf-progress" style="display: none;">
                <div class="pdf-progress-bar">
                  <div class="pdf-progress-fill" id="pdf-progress-fill" style="width: 0%"></div>
                </div>
                <div class="pdf-progress-text" id="pdf-progress-text">Starting...</div>
              </div>
            ` : `
              <div class="pdf-confirmation-unavailable">
                <div class="pdf-unavailable-icon">📄</div>
                <h4>PDF Export Not Available</h4>
                <p>${previewInfo?.reason || 'No content found to export.'}</p>
                <p class="pdf-unavailable-hint">
                  Try navigating to a page with data, such as your trip itinerary,
                  places list, or travel arrangements.
                </p>
              </div>
            `}
          </div>

          <!-- Actions -->
          <div class="pdf-confirmation-actions">
            ${previewInfo?.available ? `
              <button class="pdf-confirmation-button pdf-confirmation-cancel" onclick="window.pdfExportController.closeCurrentModal()">
                Cancel
              </button>
              <button class="pdf-confirmation-button pdf-confirmation-confirm" id="pdf-confirm-button"
                      onclick="window.pdfExportController.confirmExport()">
                Generate PDF
              </button>
            ` : `
              <button class="pdf-confirmation-button pdf-confirmation-cancel" onclick="window.pdfExportController.closeCurrentModal()">
                Close
              </button>
            `}
          </div>
        </div>
      </div>
    `;

    container.innerHTML = modalHTML;

    // Add modal styles if not already present
    this.injectModalStyles();

    // Store context for later use
    this.currentExportContext = context;
    this.currentOnConfirm = onConfirm;
    this.currentOnClose = onClose;
  }

  /**
   * Inject modal styles into document
   */
  injectModalStyles() {
    if (document.getElementById('pdf-modal-styles')) {
      return; // Already injected
    }

    const styles = document.createElement('style');
    styles.id = 'pdf-modal-styles';
    styles.textContent = `
      .pdf-confirmation-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      }
      .pdf-confirmation-modal {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 500px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
      }
      .pdf-confirmation-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px 24px 16px;
        border-bottom: 1px solid #e5e5e5;
      }
      .pdf-confirmation-header h3 {
        margin: 0;
        color: #333;
        font-size: 20px;
        font-weight: 600;
      }
      .pdf-confirmation-close {
        background: none;
        border: none;
        font-size: 28px;
        color: #999;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        border-radius: 50%;
      }
      .pdf-confirmation-content {
        padding: 24px;
      }
      .pdf-confirmation-info {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
      }
      .pdf-info-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
      }
      .pdf-info-item:last-child {
        margin-bottom: 0;
      }
      .pdf-info-label {
        font-weight: 500;
        color: #555;
      }
      .pdf-info-value {
        color: #333;
      }
      .pdf-filename {
        font-family: monospace;
        background: #fff;
        padding: 2px 6px;
        border-radius: 4px;
        border: 1px solid #ddd;
        font-size: 13px;
      }
      .pdf-confirmation-message {
        text-align: center;
        margin-bottom: 20px;
      }
      .pdf-confirmation-message p {
        color: #666;
        font-size: 16px;
        margin: 0;
      }
      .pdf-progress-container {
        margin-bottom: 20px;
      }
      .pdf-progress-bar {
        width: 100%;
        height: 6px;
        background-color: #e9ecef;
        border-radius: 3px;
        overflow: hidden;
        margin-bottom: 8px;
      }
      .pdf-progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #007bff, #0056b3);
        border-radius: 3px;
        transition: width 0.3s ease;
      }
      .pdf-progress-text {
        text-align: center;
        font-size: 14px;
        color: #666;
        font-weight: 500;
      }
      .pdf-confirmation-unavailable {
        text-align: center;
        padding: 20px;
      }
      .pdf-unavailable-icon {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.6;
      }
      .pdf-confirmation-unavailable h4 {
        color: #333;
        margin: 0 0 12px 0;
        font-size: 18px;
        font-weight: 600;
      }
      .pdf-confirmation-unavailable p {
        color: #666;
        margin: 8px 0;
        line-height: 1.5;
      }
      .pdf-unavailable-hint {
        font-size: 14px;
        color: #888;
        font-style: italic;
      }
      .pdf-confirmation-actions {
        padding: 16px 24px 24px;
        display: flex;
        gap: 12px;
        justify-content: flex-end;
        border-top: 1px solid #e5e5e5;
      }
      .pdf-confirmation-button {
        padding: 10px 20px;
        border: 2px solid;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .pdf-confirmation-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      .pdf-confirmation-cancel {
        background: white;
        color: #666;
        border-color: #ddd;
      }
      .pdf-confirmation-confirm {
        background: #007bff;
        color: white;
        border-color: #007bff;
      }
      .pdf-confirmation-confirm:hover:not(:disabled) {
        background: #0056b3;
        border-color: #0056b3;
      }
    `;
    document.head.appendChild(styles);
  }

  /**
   * Update modal progress display
   * @param {HTMLElement} container - Modal container
   * @param {number} progress - Progress percentage
   * @param {string} message - Progress message
   */
  updateModalProgress(container, progress, message) {
    const progressContainer = container.querySelector('#pdf-progress');
    const progressFill = container.querySelector('#pdf-progress-fill');
    const progressText = container.querySelector('#pdf-progress-text');
    const confirmButton = container.querySelector('#pdf-confirm-button');
    const messageElement = container.querySelector('.pdf-confirmation-message p');

    if (progressContainer) {
      progressContainer.style.display = 'block';
    }

    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }

    if (progressText) {
      progressText.textContent = message;
    }

    if (confirmButton) {
      confirmButton.disabled = true;
      confirmButton.textContent = 'Generating...';
    }

    if (messageElement) {
      messageElement.textContent = 'Generating PDF document...';
    }
  }

  /**
   * Close the current modal
   * @param {HTMLElement} container - Optional container to remove
   */
  closeModal(container = null) {
    const containerToRemove = container || this.currentModal;
    if (containerToRemove && containerToRemove.parentNode) {
      containerToRemove.parentNode.removeChild(containerToRemove);
    }
    this.currentModal = null;
    this.currentExportContext = null;
    this.currentOnConfirm = null;
    this.currentOnClose = null;
  }

  /**
   * Global methods for HTML modal interaction
   */
  closeCurrentModal() {
    if (this.currentOnClose) {
      this.currentOnClose();
    }
    this.closeModal();
  }

  async confirmExport() {
    if (this.currentOnConfirm && this.currentModal) {
      await this.currentOnConfirm();
    }
  }

  /**
   * Show export started notification
   * @param {string} pageType - Page type being exported
   */
  showExportStartedNotification(pageType) {
    console.log(`Starting PDF export for ${pageType}...`);
    // TODO: Integrate with notification system
    // Could show a toast notification or progress indicator
  }

  /**
   * Show export completed notification
   * @param {string} pageType - Page type that was exported
   * @param {string} filename - Name of exported file
   */
  showExportCompletedNotification(pageType, filename = 'PDF') {
    console.log(`PDF export completed for ${pageType}: ${filename}`);
    // TODO: Integrate with notification system
    // Could show a success toast with download confirmation

    // Alert popup removed - PDF download is notification enough for the user
  }

  /**
   * Show export error notification
   * @param {string} errorMessage - Error message to display
   */
  showExportErrorNotification(errorMessage) {
    console.error('PDF export error:', errorMessage);
    // TODO: Integrate with notification system
    // Could show an error toast with retry option

    // Show simple alert for now
    if (typeof window !== 'undefined' && window.alert) {
      window.alert(`PDF export failed:\n\n${errorMessage}\n\nPlease try again or navigate to a page with data.`);
    }
  }

  /**
   * Check if PDF export is available for the current context
   * @param {Object} context - Current application context
   * @returns {Promise<boolean>} Whether PDF export is available
   */
  async isExportAvailable(context) {
    try {
      if (!context || !context.pathname) {
        return false;
      }

      const previewInfo = await pdfExportService.getExportPreview(context);
      return previewInfo.available;

    } catch (error) {
      console.warn('Could not check PDF export availability:', error);
      return false;
    }
  }

  /**
   * Get user-friendly export description for current context
   * @param {Object} context - Current application context
   * @returns {Promise<string>} Description of what will be exported
   */
  async getExportDescription(context) {
    try {
      const previewInfo = await pdfExportService.getExportPreview(context);

      if (previewInfo.available) {
        return `Export ${previewInfo.pageType.toLowerCase()} (${previewInfo.contentSummary})`;
      } else {
        return 'PDF export not available for current page';
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return 'Export current page as PDF';
    }
  }

  /**
   * Get export preview information
   * @param {Object} context - Current application context
   * @returns {Promise<Object>} Preview information
   */
  async getExportPreview(context) {
    return await pdfExportService.getExportPreview(context);
  }
}

// Export singleton instance and make it globally available
export const pdfExportController = new PDFExportController();

// Make it globally available for HTML modal interactions
if (typeof window !== 'undefined') {
  window.pdfExportController = pdfExportController;
}
export default pdfExportController;